import { useLanguage } from '../../LanguageContext';
import ZendingTijdlijn from './ZendingTijdlijn';
/*****************************************************
Naam: CustomerAanvragenModal.jsx
Locatie: src/components/CustomerAanvragenModal.jsx
Datum: 2026-02-03
Versie: 1.6.0
Auteur: Claude + Ragnar
******************************************************
BESCHRIJVING:
Modal voor klanten om hun aanvragen te bekijken met:
- Draggable modal
- Filters direct zichtbaar (geen toggle knop)
- Status filter alleen bij "Alle Aanvragen"
- Goederen kolom toegevoegd
- Zoekfunctie
- Overzichtelijke tabel
- AKKOORD knop bij "Prijs ontvangen" status
- DATUM DIALOOG voor invullen ontbrekende datums

KLANT STATUSSEN (mapping naar interne statussen):
- Aanvraag ingediend → Prijzen nog aan te vragen, Prijzen uitgezet, Prijzen vervoerders binnen
- Prijs ontvangen → Prijs geoffreerd
- Opdracht → Opdracht ontvangen, Opdracht verzonden
- Onderweg → (later te implementeren)
- Afgerond → (later te implementeren)

CHANGELOG:
v1.6.0 (2026-02-03) - Claude
  - FIX: Datum validatie in dialoog
  - Laaddatum minimaal vandaag
  - Losdatum minimaal laaddatum
  - Bij wijzigen laaddatum wordt losdatum aangepast als nodig

v1.5.0 (2026-02-03) - Claude
  - NIEUW: Datum invul dialoog als laaddatum/losdatum ontbreken
  - Klanten kunnen nu zelf datums invullen via portal
  - Na opslaan datums wordt direct akkoord gevraagd

v1.4.0 (2026-02-03) - Claude
  - FIX: Error handling voor object en string error formats
  - Voorheen crashte de app bij datum validatie error
  - Nu wordt foutmelding correct getoond

v1.3.0 (2026-02-03) - Claude
  - NIEUW: onDataChange prop voor instant count refresh
  - FIX: Dashboard counts worden direct bijgewerkt na akkoord geven

v1.2.0 (2026-01-20) - Ragnar + Claude
  - NIEUW: Akkoord knop bij "Prijs ontvangen"
  - NIEUW: Klant kan prijs accepteren via portal
  - NIEUW: Dashboard krijgt melding bij akkoord
  - NIEUW: Status update naar "Opdracht ontvangen"

v1.1.0 (2026-01-20) - Ragnar + Claude
  - NIEUW: Draggable modal
  - NIEUW: Filters direct zichtbaar
  - NIEUW: Goederen kolom
  - GEWIJZIGD: Status rij alleen bij "Alle Aanvragen"
  - VERWIJDERD: Filter toggle knop

v1.0.0 (2026-01-19) - Ragnar + Claude
  - Eerste versie
  - Status filter, datum filter, zoekfunctie
******************************************************/

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { io } from 'socket.io-client';
import { 
  X, 
  Search, 
  Calendar,
  MapPin,
  Package,
  Clock,
  FileText,
  Truck,
  CheckCircle,
  CheckCircle2,
  Check,
  ChevronDown,
  ChevronUp,
  Euro,
  Box,
  Printer,
  Mail,
  Loader2,
  RotateCcw
, XCircle } from 'lucide-react';

// Mapping van klant-statussen naar interne statussen

// v4.1.0: Prijs formattering met komma en 2 decimalen
const formatPrijs = (prijs) => {
  if (!prijs && prijs !== 0) return '-';
  const num = parseFloat(String(prijs).replace(',', '.'));
  if (isNaN(num)) return '-';
  return num.toFixed(2).replace('.', ',');
};

const KLANT_STATUS_MAPPING = {
  'Aanvraag ingediend': ['Prijzen nog aan te vragen', 'Prijzen uitgezet', 'Prijzen vervoerders binnen'],
  'Prijs ontvangen': ['Prijs geoffreerd'],
  'Akkoord gegeven': ['Opdracht ontvangen'],
  'Opdracht bevestigd': ['Opdracht verzonden', 'Opdracht bevestigd'],
  'Opdracht': ['Opdracht ontvangen', 'Opdracht verzonden', 'Opdracht bevestigd'], // Combined filter
  'Onderweg': ['Onderweg'],
  'Afgerond': ['Afgeleverd', 'Afgerond'],
  'Geannuleerd': ['Afgeschreven']
};

// Kleuren per klant-status
const KLANT_STATUS_COLORS = {
  'Aanvraag ingediend': { bg: 'bg-blue-100', text: 'text-blue-800', icon: Clock },
  'Prijs ontvangen': { bg: 'bg-blue-100', text: 'text-blue-900', icon: Euro },
  'Akkoord gegeven': { bg: 'bg-orange-100', text: 'text-orange-800', icon: Clock },
  'Opdracht bevestigd': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
  'Opdracht': { bg: 'bg-orange-100', text: 'text-orange-800', icon: FileText },
  'Onderweg': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Truck },
  'Afgerond': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
  'Geannuleerd': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle }
};

// Functie om interne status te mappen naar klant-status
const mapToKlantStatus = (interneStatus) => {
  if (!interneStatus) return 'Aanvraag ingediend';
  
  const status = interneStatus.toLowerCase();
  
  // Akkoord gegeven (klant heeft akkoord gegeven, wacht op bevestiging)
  if (status.includes('opdracht ontvangen')) {
    return 'Akkoord gegeven';
  }
  
  // Opdracht bevestigd (SendWell heeft bevestigd)
  if (status.includes('opdracht verzonden') || status.includes('opdracht bevestigd')) {
    return 'Opdracht bevestigd';
  }
  
  // Afgeschreven/geannuleerd
  if (status.includes('afgeschreven')) return 'Geannuleerd';

  for (const [klantStatus, interneStatussen] of Object.entries(KLANT_STATUS_MAPPING)) {
    if (klantStatus === 'Opdracht') continue; // Skip combined filter
    if (interneStatussen.some(s => status.includes(s.toLowerCase()))) {
      return klantStatus;
    }
  }
  
  return 'Aanvraag ingediend';
};

// Functie om goederen samen te vatten
// Format: 5x PLTS Flacons 1.20x1.00x1.50m 500kg
// Skip lege regels (alleen aantal zonder soort/omschrijving)
const formatGoederen = (aanvraag) => {
  const goederen = [];
  
  for (let i = 1; i <= 5; i++) {
    const aantal = aanvraag[`Goed${i}_Aantal`];
    const soort = aanvraag[`Goed${i}_Soort`];
    const omschrijving = aanvraag[`Goed${i}_Omschrijving`];
    const lengte = aanvraag[`Goed${i}_Lengte`];
    const breedte = aanvraag[`Goed${i}_Breedte`];
    const hoogte = aanvraag[`Goed${i}_Hoogte`];
    const gewicht = aanvraag[`Goed${i}_Gewicht`];
    
    // Skip als er geen soort EN geen omschrijving is (alleen aantal is niet genoeg)
    if (!soort && !omschrijving) continue;
    
    let parts = [];
    
    // Aantal + Soort
    if (aantal) parts.push(`${aantal}x`);
    if (soort) parts.push(soort);
    if (omschrijving) parts.push(omschrijving);
    
    // Afmetingen (LxBxH)
    if (lengte && breedte && hoogte) {
      parts.push(`${lengte}x${breedte}x${hoogte}m`);
    }
    
    // Gewicht
    if (gewicht) {
      parts.push(`${gewicht}kg`);
    }
    
    if (parts.length > 0) {
      goederen.push(parts.join(' '));
    }
  }
  
  return goederen.length > 0 ? goederen.join(', ') : '-';
};

const CustomerAanvragenModal = ({
  show, 
  onClose, 
  onDataChange = null,
  initialStatusFilter = null,
  title = 'Mijn Aanvragen'
}) => {
  const { t } = useLanguage();
  const [aanvragen, setAanvragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(initialStatusFilter || 'all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Sorting
  const [sortField, setSortField] = useState('Datum');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Expanded row for mobile details
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Accept quote state
  const [acceptingQuote, setAcceptingQuote] = useState(null); // aanvraagId being accepted
  const [acceptSuccess, setAcceptSuccess] = useState(null);
  const [acceptError, setAcceptError] = useState(null);
  
  // Datum invullen dialoog state
  const [showDatumDialog, setShowDatumDialog] = useState(false);
  const [detailAanvraag, setDetailAanvraag] = useState(null);
  const [detailPos, setDetailPos] = useState({ x: Math.max(50, (window.innerWidth - 672) / 2), y: 40 });
  const [detailTrackerNotes, setDetailTrackerNotes] = useState('');
  const [showFactuurPdf, setShowFactuurPdf] = useState('');
  const [datumDialogAanvraag, setDatumDialogAanvraag] = useState(null);
  const [dialogLaadDatum, setDialogLaadDatum] = useState('');
  const [dialogLosDatum, setDialogLosDatum] = useState('');
  const [savingDatums, setSavingDatums] = useState(false);
  
  // Draggable state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // Determine if we should show status filter (only for "Alle Aanvragen")
  const showStatusFilter = !initialStatusFilter;
  
  // Determine if we should show price column (NOT for "Aanvraag ingediend")
  const showPriceColumn = initialStatusFilter !== 'Aanvraag ingediend';

  // Load aanvragen when modal opens
  useEffect(() => {
    if (show) {
      loadAanvragen();
      // Reset filters when modal opens with new initialStatusFilter
      if (initialStatusFilter) {
        setSelectedStatus(initialStatusFilter);
      } else {
        setSelectedStatus('all');
      }
      // Reset position
      setPosition({ x: 0, y: 0 });
    }
  }, [show, initialStatusFilter]);


  // Socket listener voor instant portal refresh
  useEffect(() => {
    const socket = io(window.location.origin, { transports: ['websocket', 'polling'] });
    socket.on('aanvraag-updated', () => { loadAanvragen(); });
    socket.on('prijs-geoffreerd', () => { loadAanvragen(); });
    return () => { socket.disconnect(); };
  }, []);
  // Draggable handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.modal-header')) {
      setIsDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left - position.x,
        y: e.clientY - rect.top - position.y
      });
    }
  };

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => {
        if (modalRef.current) {
          const rect = modalRef.current.getBoundingClientRect();
          const newX = e.clientX - dragOffset.x - (window.innerWidth - rect.width) / 2;
          const newY = e.clientY - dragOffset.y - (window.innerHeight - rect.height) / 2;
          setPosition({ x: newX, y: newY });
        }
      };

      const handleMouseUp = () => {
        setIsDragging(false);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const loadAanvragen = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/customer/aanvragen', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Kon aanvragen niet laden');
      }
      
      const data = await response.json();
      
      if (data.success) {
        const mappedAanvragen = (data.aanvragen || []).map(a => ({
          ...a,
          KlantStatus: mapToKlantStatus(a.Status)
        }));
        setAanvragen(mappedAanvragen);
      } else {
        throw new Error(typeof data.error === 'object' ? (data.error.message || 'Onbekende fout') : (data.error || 'Onbekende fout'));
      }
    } catch (err) {
      console.error('Load aanvragen error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle accept quote (klant geeft akkoord)
  const handleAcceptQuote = async (aanvraagId, e) => {
    e.stopPropagation(); // Prevent row expansion on mobile
    
    if (acceptingQuote) return; // Already processing
    
    // Vind de aanvraag om datums te checken
    const aanvraag = aanvragen.find(a => a.AanvraagID === aanvraagId);
    
    // Check of datums ontbreken
    const isValidDatum = (datum) => {
      if (!datum) return false;
      const str = String(datum).trim();
      return str !== '' && str !== '-' && str !== 'null' && str !== 'undefined';
    };
    
    const heeftLaadDatum = isValidDatum(aanvraag?.LaadDatum);
    const heeftLosDatum = isValidDatum(aanvraag?.LosDatum);
    
    // Als datums ontbreken, toon dialoog
    if (!heeftLaadDatum || !heeftLosDatum) {
      setDatumDialogAanvraag(aanvraag);
      setDialogLaadDatum(heeftLaadDatum ? aanvraag.LaadDatum : '');
      setDialogLosDatum(heeftLosDatum ? aanvraag.LosDatum : '');
      setShowDatumDialog(true);
      return;
    }
    
    // Datums zijn ingevuld, vraag bevestiging
    if (!window.confirm(`Weet u zeker dat u akkoord wilt geven voor aanvraag #${aanvraagId}?`)) {
      return;
    }
    
    // Voer accept quote uit
    await executeAcceptQuote(aanvraagId);
  };
  
  // Daadwerkelijke accept quote uitvoering
  const executeAcceptQuote = async (aanvraagId) => {
    setAcceptingQuote(aanvraagId);
    setAcceptSuccess(null);
    setAcceptError(null);
    
    try {
      const response = await fetch('/api/customer/accept-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ aanvraagId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAcceptSuccess(data.message || 'Opdracht succesvol bevestigd!');
        // Reload aanvragen to reflect new status
        loadAanvragen();
        // Refresh parent dashboard counts
        if (onDataChange) onDataChange();
      } else {
        // Handle both string and object error formats
        const errorMessage = typeof data.error === 'string' 
          ? data.error 
          : (data.error?.message || 'Er is iets misgegaan');
        setAcceptError(errorMessage);
      }
    } catch (err) {
      console.error('Accept quote error:', err);
      setAcceptError('Netwerkfout. Probeer het opnieuw.');
    } finally {
      setAcceptingQuote(null);
    }
  };
  
  // Handle datum dialog opslaan en akkoord geven
  const handleDatumDialogSubmit = async () => {
    if (!dialogLaadDatum || !dialogLosDatum) {
      setAcceptError('Vul zowel laaddatum als losdatum in.');
      return;
    }
    
    setSavingDatums(true);
    setAcceptError(null);
    
    try {
      // Eerst datums opslaan
      const response = await fetch('/api/customer/update-datums', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          aanvraagId: datumDialogAanvraag.AanvraagID,
          laadDatum: dialogLaadDatum,
          losDatum: dialogLosDatum
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Sluit dialoog en geef direct akkoord
        setShowDatumDialog(false);
        setDatumDialogAanvraag(null);
        await executeAcceptQuote(datumDialogAanvraag.AanvraagID);
      } else {
        const errorMessage = typeof data.error === 'string' 
          ? data.error 
          : (data.error?.message || 'Fout bij opslaan datums');
        setAcceptError(errorMessage);
      }
    } catch (err) {
      console.error('Save datums error:', err);
      setAcceptError('Netwerkfout. Probeer het opnieuw.');
    } finally {
      setSavingDatums(false);
    }
  };

  // Filter and sort aanvragen
  const filteredAanvragen = useMemo(() => {
    let result = [...aanvragen];
    
    // Status filter
    if (selectedStatus !== 'all') {
      // Special case: "Opdracht" filter should match both "Akkoord gegeven" and "Opdracht bevestigd"
      if (selectedStatus === 'Opdracht') {
        result = result.filter(a => 
          a.KlantStatus === 'Akkoord gegeven' || 
          a.KlantStatus === 'Opdracht bevestigd'
        );
      } else {
        result = result.filter(a => a.KlantStatus === selectedStatus);
      }
    }
    
    // Date filter
    if (startDate) {
      const start = new Date(startDate);
      result = result.filter(a => {
        const aanvraagDate = new Date(a.Datum);
        return aanvraagDate >= start;
      });
    }
    
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter(a => {
        const aanvraagDate = new Date(a.Datum);
        return aanvraagDate <= end;
      });
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(a => 
        (a.AanvraagID?.toString() || '').includes(term) ||
        (a.LaadAdres || '').toLowerCase().includes(term) ||
        (a.LaadPlaats || '').toLowerCase().includes(term) ||
        (a.LaadLand || '').toLowerCase().includes(term) ||
        (a.LosAdres || '').toLowerCase().includes(term) ||
        (a.LosPlaats || '').toLowerCase().includes(term) ||
        (a.LosLand || '').toLowerCase().includes(term) ||
        (a.Referentie1 || '').toLowerCase().includes(term) ||
        (a.Referentie2 || '').toLowerCase().includes(term)
      );
    }
    
    // Sorting
    result.sort((a, b) => {
      let compareA, compareB;
      
      switch (sortField) {
        case 'AanvraagID':
          compareA = a.AanvraagID || 0;
          compareB = b.AanvraagID || 0;
          break;
        case 'Datum':
          compareA = new Date(a.Datum || 0);
          compareB = new Date(b.Datum || 0);
          break;
        case 'LaadDatum':
          compareA = new Date(a.LaadDatum || 0);
          compareB = new Date(b.LaadDatum || 0);
          break;
        case 'KlantStatus':
          compareA = a.KlantStatus || '';
          compareB = b.KlantStatus || '';
          break;
        default:
          compareA = a[sortField] || '';
          compareB = b[sortField] || '';
      }
      
      if (compareA < compareB) return sortDirection === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [aanvragen, selectedStatus, startDate, endDate, searchTerm, sortField, sortDirection]);

  // Count per status
  const statusCounts = useMemo(() => {
    const counts = {};
    Object.keys(KLANT_STATUS_MAPPING).forEach(status => {
      counts[status] = aanvragen.filter(a => a.KlantStatus === status).length;
    });
    return counts;
  }, [aanvragen]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return date;
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    if (showStatusFilter) {
      setSelectedStatus('all');
    }
    setStartDate('');
    setEndDate('');
  };

  // Print functie - gebruikt hidden iframe (geen zichtbaar window)
  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title} - Afdrukken</title>
        <style>
          @page { size: landscape; margin: 1cm; }
          @media print { 
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 11px;
            margin: 0;
            padding: 20px;
          }
          h1 { 
            font-size: 18px; 
            margin-bottom: 5px;
            color: #7c3aed;
          }
          .subtitle {
            font-size: 12px;
            color: #666;
            margin-bottom: 20px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 10px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 8px; 
            text-align: left; 
          }
          th { 
            background-color: #f3f4f6; 
            font-weight: bold;
            font-size: 10px;
            text-transform: uppercase;
          }
          tr:nth-child(even) { background-color: #f9fafb; }
          .print-date {
            text-align: right;
            font-size: 10px;
            color: #999;
            margin-bottom: 10px;
          }
        </style>
      </head>
      <body>
        <div class="print-date">Afgedrukt op: ${new Date().toLocaleDateString('nl-NL')} ${new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}</div>
        <h1>${title}</h1>
        <div class="subtitle">${filteredAanvragen.length} aanvragen</div>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Datum</th>
              <th>Laden</th>
              <th>Lossen</th>
              <th>Goederen</th>
              ${showPriceColumn ? `<th>{t('prijs')}</th>` : ''}
            </tr>
          </thead>
          <tbody>
            ${filteredAanvragen.map(aanvraag => `
              <tr>
                <td><strong>#${aanvraag.AanvraagID}</strong></td>
                <td>${formatDate(aanvraag.Datum)}</td>
                <td>${aanvraag.LaadPlaats || aanvraag.LaadAdres || '-'}<br><small>${aanvraag.LaadLand || ''}</small></td>
                <td>${aanvraag.LosPlaats || aanvraag.LosAdres || '-'}<br><small>${aanvraag.LosLand || ''}</small></td>
                <td>${formatGoederen(aanvraag)}</td>
                ${showPriceColumn ? `<td>${aanvraag.KlantPrijs ? '€ ' + formatPrijs(aanvraag.KlantPrijs) : '-'}</td>` : ''}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    // Maak hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.left = '-9999px';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printContent);
    iframeDoc.close();

    // Wacht tot content geladen is, dan print
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
      // Verwijder iframe na printen
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <div 
        ref={modalRef}
        className="bg-white shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        onMouseDown={handleMouseDown}
      >
        
        {/* Header - Draggable */}
        <div className="modal-header bg-gradient-to-r from-blue-900 to-blue-800 text-white p-6 flex justify-between items-center cursor-grab active:cursor-grabbing select-none">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">{title}</h2>
              <p className="text-sm opacity-90">
                {filteredAanvragen.length} {t('van') || 'van'} {aanvragen.length} {t('nav_aanvragen').toLowerCase()}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-white/20 px-3 py-2 transition-colors flex items-center gap-2 rounded"
          >
            <span className="hidden sm:inline text-sm font-medium">{t('sluiten')}</span>
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filters - Direct zichtbaar */}
        <div className="bg-gray-50 border-b border-gray-200 p-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Zoek op ID, locatie, referentie..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-transparent"
            />
          </div>

          {/* Status Filter - Alleen bij "Alle Aanvragen" */}
          {showStatusFilter && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Status
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedStatus('all')}
                  className={`px-4 py-2 font-medium transition-all ${
                    selectedStatus === 'all'
                      ? 'bg-blue-900 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Alle ({aanvragen.length})
                </button>
                {Object.keys(KLANT_STATUS_MAPPING).map(status => {
                  const config = KLANT_STATUS_COLORS[status];
                  const Icon = config.icon;
                  return (
                    <button
                      key={status}
                      onClick={() => setSelectedStatus(status)}
                      className={`px-4 py-2 font-medium transition-all flex items-center gap-2 ${
                        selectedStatus === status
                          ? 'bg-blue-900 text-white'
                          : `${config.bg} ${config.text} hover:opacity-80`
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {status} ({statusCounts[status] || 0})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Date Filter */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Van datum
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Tot datum
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-transparent"
              />
            </div>
          </div>

          {/* Reset Filters */}
          {(selectedStatus !== 'all' || startDate || endDate || searchTerm) && (
            <button
              onClick={resetFilters}
              className="text-blue-900 hover:text-blue-800 font-medium text-sm"
            >
              ✕ Reset alle filters
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-red-500 text-lg mb-2">⚠️ Fout bij laden</div>
              <p className="text-gray-600">{error}</p>
              <button
                onClick={loadAanvragen}
                className="mt-4 px-4 py-2 bg-blue-900 text-white hover:bg-blue-800"
              >
                Opnieuw proberen
              </button>
            </div>
          ) : filteredAanvragen.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Package className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-600 text-lg">{t('geen_aanvragen')}</p>
              {(selectedStatus !== 'all' || startDate || endDate || searchTerm) && (
                <button
                  onClick={resetFilters}
                  className="mt-4 text-blue-900 hover:text-blue-800 font-medium"
                >
                  Reset filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Success/Error messages for accept quote */}
              {acceptSuccess && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  {acceptSuccess}
                </div>
              )}
              {acceptError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 flex items-center gap-2 text-red-700">
                  <X className="w-5 h-5" />
                  {acceptError}
                </div>
              )}
              
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('AanvraagID')}
                      >
                        <div className="flex items-center gap-1">
                          ID
                          {sortField === 'AanvraagID' && (
                            sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('Datum')}
                      >
                        <div className="flex items-center gap-1">
                          Datum
                          {sortField === 'Datum' && (
                            sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Laden
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Lossen
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center gap-1">
                          <Box className="w-4 h-4" />
                          Goederen
                        </div>
                      </th>
                      {showPriceColumn && (
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          {t('prijs')}
                        </th>
                      )}
                      {/* Status kolom alleen bij "Opdracht" filter */}
                      {initialStatusFilter === 'Opdracht' && (
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          {t('status')}
                        </th>
                      )}
                      {/* Actie kolom alleen bij "Prijs ontvangen" */}
                      {initialStatusFilter === 'Prijs ontvangen' && (
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          {t('actie')}
                        </th>
                      )}
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-16">
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredAanvragen.map((aanvraag, index) => {
                      // statusConfig alleen nodig voor mobile view, niet hier
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span 
                              className="font-semibold text-blue-900 hover:text-blue-600 cursor-pointer hover:underline"
                              onClick={() => {
                              setDetailAanvraag(aanvraag);
                              setDetailTrackerNotes('');
                              setDetailPos({ x: Math.max(50, (window.innerWidth - 672) / 2), y: 40 });
                              if (aanvraag.TrackerID) {
                                fetch('/api/customer/tracker-notes/' + aanvraag.TrackerID)
                                  .then(r => r.json())
                                  .then(d => { if (d.notes) setDetailTrackerNotes(d.notes); })
                                  .catch(() => {});
                              }
                            }}
                              title="Klik voor details"
                            >
                              #{aanvraag.AanvraagID}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                            {formatDate(aanvraag.Datum)}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <div className="text-sm">
                                <div className="font-medium text-gray-900">
                                  {aanvraag.LaadPlaats || aanvraag.LaadAdres || '-'}
                                </div>
                                <div className="text-gray-500">{aanvraag.LaadLand || '-'}</div>
                                <div className="text-gray-400 text-xs">{formatDate(aanvraag.LaadDatum)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <div className="text-sm">
                                <div className="font-medium text-gray-900">
                                  {aanvraag.LosPlaats || aanvraag.LosAdres || '-'}
                                </div>
                                <div className="text-gray-500">{aanvraag.LosLand || '-'}</div>
                                <div className="text-gray-400 text-xs">{formatDate(aanvraag.LosDatum)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-700 max-w-xs" title={formatGoederen(aanvraag)}>
                              {formatGoederen(aanvraag)}
                            </div>
                          </td>
                          {showPriceColumn && (
                            <td className="px-4 py-4 whitespace-nowrap">
                              {aanvraag.KlantPrijs ? (
                                <span className="font-semibold text-gray-900">
                                  € {formatPrijs(aanvraag.KlantPrijs)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          )}
                          {/* Status kolom bij "Opdracht" filter */}
                          {initialStatusFilter === 'Opdracht' && (
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                              {aanvraag.KlantStatus === 'Akkoord gegeven' ? (
                                <div className="inline-flex flex-col items-center">
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                                    <Clock className="w-3 h-3" />
                                    {t('status_akkoord_gegeven')}
                                  </span>
                                  <span className="text-xs text-gray-500 mt-1">{t('wacht_op_bevestiging')}</span>
                                </div>
                              ) : aanvraag.KlantStatus === 'Opdracht bevestigd' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                  <CheckCircle className="w-3 h-3" />
                                  Opdracht bevestigd
                                </span>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                          )}
                          {/* Actie kolom met Akkoord knop - alleen bij "Prijs ontvangen" */}
                          {initialStatusFilter === 'Prijs ontvangen' && (
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                              {aanvraag.KlantPrijs && aanvraag.KlantStatus === 'Prijs ontvangen' ? (
                                <button
                                  onClick={(e) => handleAcceptQuote(aanvraag.AanvraagID, e)}
                                  disabled={acceptingQuote === aanvraag.AanvraagID}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {acceptingQuote === aanvraag.AanvraagID ? (
                                    <>
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      {t('bezig')}
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle2 className="w-4 h-4" />
                                      {t('akkoord_geven')}
                                    </>
                                  )}
                                </button>
                              ) : aanvraag.KlantBevestigdOp ? (
                                <span className="inline-flex items-center gap-1 text-green-600 text-sm font-medium">
                                  <CheckCircle className="w-4 h-4" />
                                  {t('status_opdracht_bevestigd')}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                          )}
                          <td className="px-3 py-4 text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                try { sessionStorage.setItem('kopieerAanvraag', JSON.stringify(aanvraag)); } catch(e) {} window.location.href = '/customer/new-request';
                                onClose();
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-900 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                              title="Kopieer aanvraag"
                            >
                              <RotateCcw className="w-3 h-3" />
                              Kopieer
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden divide-y divide-gray-200">
                {filteredAanvragen.map((aanvraag, index) => {
                  const statusConfig = KLANT_STATUS_COLORS[aanvraag.KlantStatus] || KLANT_STATUS_COLORS['Aanvraag ingediend'];
                  const StatusIcon = statusConfig.icon;
                  const isExpanded = expandedRow === index;
                  
                  return (
                    <div 
                      key={index} 
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedRow(isExpanded ? null : index)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-blue-900">
                          #{aanvraag.AanvraagID}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusConfig.bg} ${statusConfig.text}`}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {aanvraag.KlantStatus}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>{formatDate(aanvraag.Datum)}</span>
                        {showPriceColumn && aanvraag.KlantPrijs && (
                          <span className="font-semibold text-gray-900">€ {formatPrijs(aanvraag.KlantPrijs)}</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-green-500" />
                        <span className="text-gray-700">{aanvraag.LaadPlaats || aanvraag.LaadLand || '-'}</span>
                        <span className="text-gray-400">→</span>
                        <MapPin className="w-4 h-4 text-red-500" />
                        <span className="text-gray-700">{aanvraag.LosPlaats || aanvraag.LosLand || '-'}</span>
                      </div>

                      {initialStatusFilter === 'Prijs ontvangen' && aanvraag.KlantPrijs && aanvraag.KlantStatus === 'Prijs ontvangen' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleAcceptQuote(aanvraag.AanvraagID, e); }}
                          disabled={acceptingQuote === aanvraag.AanvraagID}
                          className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white font-bold rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          {acceptingQuote === aanvraag.AanvraagID ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Bezig...</>
                          ) : (
                            <><CheckCircle2 className="w-4 h-4" /> {t('akkoord_geven')}</>
                          )}
                        </button>
                      )}
                      {initialStatusFilter === 'Prijs ontvangen' && aanvraag.KlantBevestigdOp && (
                        <div className="mt-2 flex items-center justify-center gap-1 text-green-600 font-medium text-sm">
                          <CheckCircle className="w-4 h-4" /> {t('status_opdracht_bevestigd')}
                        </div>
                      )}
                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="text-gray-500 text-xs mb-1">{t('laaddatum')}</div>
                              <div className="font-medium">{formatDate(aanvraag.LaadDatum)}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs mb-1">{t('losdatum')}</div>
                              <div className="font-medium">{formatDate(aanvraag.LosDatum)}</div>
                            </div>
                          </div>
                          <div className="text-sm">
                            <div className="text-gray-500 text-xs mb-1">{t('goederen')}</div>
                            <div className="font-medium">{formatGoederen(aanvraag)}</div>
                          </div>
                          {aanvraag.Referentie1 && (
                            <div className="text-sm">
                              <div className="text-gray-500 text-xs mb-1">{t('referentie')}</div>
                              <div className="font-medium">{aanvraag.Referentie1}</div>
                            </div>
                          )}
                          {/* Akkoord knop voor mobile - alleen bij "Prijs ontvangen" */}
                          {initialStatusFilter === 'Prijs ontvangen' && aanvraag.KlantPrijs && aanvraag.KlantStatus === 'Prijs ontvangen' && (
                            <div className="pt-2">
                              <button
                                onClick={(e) => handleAcceptQuote(aanvraag.AanvraagID, e)}
                                disabled={acceptingQuote === aanvraag.AanvraagID}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {acceptingQuote === aanvraag.AanvraagID ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('bezig')}
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 className="w-4 h-4" />
                                    {t('akkoord_geven')}
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                          {initialStatusFilter === 'Prijs ontvangen' && aanvraag.KlantBevestigdOp && (
                            <div className="pt-2 flex items-center justify-center gap-1 text-green-600 font-medium">
                              <CheckCircle className="w-4 h-4" />
                              {t('status_opdracht_bevestigd')}
                            </div>
                          )}
                          <div className="pt-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                try { sessionStorage.setItem('kopieerAanvraag', JSON.stringify(aanvraag)); } catch(e) {} window.location.href = '/customer/new-request';
                                onClose();
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-900 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              Kopieer aanvraag
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-between">
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
            >
              <Printer className="w-5 h-5" />
              Afdrukken
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors font-medium"
          >
            Sluiten
          </button>
        </div>
      </div>
      
      {/* Datum Invullen Dialoog */}
      {showDatumDialog && datumDialogAanvraag && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
          <div className="bg-white shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-6 py-4">
              <h3 className="text-lg font-bold">📅 Datums Invullen</h3>
              <p className="text-blue-200 text-sm">Aanvraag #{datumDialogAanvraag.AanvraagID}</p>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-gray-600 text-sm">
                Vul de laad- en losdatum in om de opdracht te kunnen bevestigen.
              </p>
              
              {/* Route info */}
              <div className="bg-gray-50 p-3 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="font-medium">{t('route')}:</span>
                  <span>{datumDialogAanvraag.LaadPlaats || datumDialogAanvraag.LaadLand}</span>
                  <span>→</span>
                  <span>{datumDialogAanvraag.LosPlaats || datumDialogAanvraag.LosLand}</span>
                </div>
              </div>
              
              {/* Datum velden */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Laaddatum *
                  </label>
                  <input
                    type="date"
                    value={dialogLaadDatum}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      setDialogLaadDatum(e.target.value);
                      // Als losdatum eerder is dan nieuwe laaddatum, reset losdatum
                      if (dialogLosDatum && e.target.value > dialogLosDatum) {
                        setDialogLosDatum(e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Losdatum *
                  </label>
                  <input
                    type="date"
                    value={dialogLosDatum}
                    min={dialogLaadDatum || new Date().toISOString().split('T')[0]}
                    onChange={(e) => setDialogLosDatum(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
                  />
                </div>
              </div>
              
              {/* Error message */}
              {acceptError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm">
                  {acceptError}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDatumDialog(false);
                  setDatumDialogAanvraag(null);
                  setAcceptError(null);
                }}
                disabled={savingDatums}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleDatumDialogSubmit}
                disabled={savingDatums || !dialogLaadDatum || !dialogLosDatum}
                className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {savingDatums ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {t('akkoord_geven') + ' & ' + t('opslaan')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    


              {/* Opmerkingen */}




      {/* Detail aanvraag overlay */}
      {detailAanvraag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-10" style={{ zIndex: 10000 }}>
          <div
            className="bg-white shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-auto border-2 border-blue-900"
            style={{ position: 'absolute', left: detailPos.x + 'px', top: detailPos.y + 'px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="sticky top-0 z-10 bg-blue-900 text-white p-4 flex justify-between items-center cursor-move select-none"
              onMouseDown={(e) => {
                const startX = e.clientX - detailPos.x;
                const startY = e.clientY - detailPos.y;
                const onMove = (ev) => setDetailPos({ x: ev.clientX - startX, y: ev.clientY - startY });
                const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              }}
            >
              <h3 className="text-xl font-bold">Aanvraag #{detailAanvraag.AanvraagID}</h3>
              <button onClick={() => setDetailAanvraag(null)} className="w-8 h-8 flex items-center justify-center hover:bg-blue-800 transition-colors text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-4">

              {/* Zendingstijdlijn */}
              <div style={{ margin: '-20px -20px 0', borderRadius: 0 }}>
                <ZendingTijdlijn aanvraag={detailAanvraag} />
              </div>

              {/* Route */}
              <div className="border-2 border-blue-900 overflow-hidden">
                <div className="bg-blue-900 text-white px-4 py-2 font-bold text-sm">{t('route').toUpperCase()}</div>
                <div className="grid grid-cols-2 gap-0 divide-x divide-gray-200">
                  <div className="p-4">
                    <div className="text-xs font-semibold text-gray-400 uppercase mb-1">{t('laadplaats')}</div>
                    <div className="font-bold text-gray-900 text-lg">{detailAanvraag.LaadPlaats || '-'}</div>
                    <div className="text-sm text-gray-600">{detailAanvraag.LaadAdres || ''}</div>
                    <div className="text-sm text-gray-600">{detailAanvraag.LaadPostcode || ''} {detailAanvraag.LaadLand || ''}</div>
                    {detailAanvraag.LaadDatum && <div className="text-sm text-blue-700 mt-2 font-medium">Datum: {formatDate(detailAanvraag.LaadDatum)}</div>}
                    {detailAanvraag.LaadBijzonderheden && <div className="text-sm text-orange-600 mt-1 italic border-l-2 border-orange-400 pl-2">{detailAanvraag.LaadBijzonderheden}</div>}
                  </div>
                  <div className="p-4">
                    <div className="text-xs font-semibold text-gray-400 uppercase mb-1">{t('losplaats')}</div>
                    <div className="font-bold text-gray-900 text-lg">{detailAanvraag.LosPlaats || '-'}</div>
                    <div className="text-sm text-gray-600">{detailAanvraag.LosAdres || ''}</div>
                    <div className="text-sm text-gray-600">{detailAanvraag.LosPostcode || ''} {detailAanvraag.LosLand || ''}</div>
                    {detailAanvraag.LosDatum && <div className="text-sm text-blue-700 mt-2 font-medium">Datum: {formatDate(detailAanvraag.LosDatum)}</div>}
                    {detailAanvraag.LosBijzonderheden && <div className="text-sm text-orange-600 mt-1 italic border-l-2 border-orange-400 pl-2">{detailAanvraag.LosBijzonderheden}</div>}
                  </div>
                </div>
              </div>

              {/* Goederen */}
              <div className="border-2 border-blue-900 overflow-hidden">
                <div className="bg-blue-900 text-white px-4 py-2 font-bold text-sm">{t('goederen').toUpperCase()}</div>
                <div className="p-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-blue-900">
                        <th className="text-left py-2 px-1 font-bold text-blue-900">{t('aantal')}</th>
                        <th className="text-left py-2 px-1 font-bold text-blue-900">{t('soort')}</th>
                        <th className="text-left py-2 px-1 font-bold text-blue-900">{t('omschrijving')}</th>
                        <th className="text-left py-2 px-1 font-bold text-blue-900">{t('afmetingen')}</th>
                        <th className="text-left py-2 px-1 font-bold text-blue-900">{t('gewicht')}</th>
                        <th className="text-left py-2 px-1 font-bold text-blue-900">"Stack"</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const rows = [];
                        for (let i = 1; i <= 5; i++) {
                          const soort = detailAanvraag['Goed' + i + '_Soort'];
                          const omschrijving = detailAanvraag['Goed' + i + '_Omschrijving'];
                          if (!soort && !omschrijving) continue;
                          rows.push(
                            <tr key={i} className="border-t border-gray-200">
                              <td className="py-2 px-1">{detailAanvraag['Goed' + i + '_Aantal'] || 1}</td>
                              <td className="py-2 px-1 font-medium">{soort || '-'}</td>
                              <td className="py-2 px-1">{omschrijving || '-'}</td>
                              <td className="py-2 px-1">
                                {detailAanvraag['Goed' + i + '_Lengte'] && detailAanvraag['Goed' + i + '_Breedte'] && detailAanvraag['Goed' + i + '_Hoogte']
                                  ? detailAanvraag['Goed' + i + '_Lengte'] + ' x ' + detailAanvraag['Goed' + i + '_Breedte'] + ' x ' + detailAanvraag['Goed' + i + '_Hoogte'] + ' m'
                                  : '-'}
                              </td>
                              <td className="py-2 px-1">{detailAanvraag['Goed' + i + '_Gewicht'] ? detailAanvraag['Goed' + i + '_Gewicht'] + ' kg' : '-'}</td>
                              <td className="py-2 px-1">{detailAanvraag['Goed' + i + '_Stackable'] === 'J' ? 'Ja' : 'Nee'}</td>
                            </tr>
                          );
                        }
                        return rows.length > 0 ? rows : <tr><td colSpan="6" className="py-3 text-gray-400 italic text-center">{t('geen_goederen')}</td></tr>;
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Specialiteiten */}
              {(() => {
                const services = ['Express','Klep','ADR','Kooiaap','Parcel','Groupage','LTL','FTL','Customs','LastMile','Geconditioneerd']
                  .filter(s => detailAanvraag[s] === 'J');
                if (services.length === 0) return null;
                return (
                  <div className="border-2 border-blue-900 overflow-hidden">
                    <div className="bg-blue-900 text-white px-4 py-2 font-bold text-sm">{t('specialiteiten').toUpperCase()}</div>
                    <div className="p-3 flex flex-wrap gap-2">
                      {services.map(s => (<span key={s} className="px-4 py-1.5 bg-blue-100 text-blue-900 text-sm font-semibold border border-blue-300">{s}</span>))}
                    </div>
                  </div>
                );
              })()}

              {/* Prijs */}
              {detailAanvraag.KlantPrijs && (
                <div className="border-2 border-green-600 overflow-hidden">
                  <div className="bg-green-600 text-white px-4 py-2 font-bold text-sm">{t('prijs_excl_btw').toUpperCase()}</div>
                  <div className="p-5 text-center bg-green-50">
                    <div className="text-4xl font-bold text-green-700">&#8364; {formatPrijs(detailAanvraag.KlantPrijs)}</div>
                  </div>
                </div>
              )}

              {/* Tracker */}
              {detailAanvraag.TrackerID && (
                <div className="border-2 border-blue-900 overflow-hidden">
                  <div className="bg-blue-900 text-white px-4 py-2 font-bold text-sm">{t('tracker').toUpperCase()}</div>
                  <div className="p-4">
                    <div className="bg-blue-50 border-2 border-blue-900 p-4 text-center">
                      <div className="text-xs text-gray-500 mb-1">{t('tracker_plaatsen') || 'Tracker plaatsen'}</div>
                      <span className="text-2xl font-bold text-blue-900 tracking-widest">{detailTrackerNotes ? detailTrackerNotes.toUpperCase() : detailAanvraag.TrackerID}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Factuur */}
              {detailAanvraag.FactuurNr && (
                <div className="border-2 border-blue-900 overflow-hidden">
                  <div className="bg-blue-900 text-white px-4 py-2 font-bold text-sm">{t('factuur').toUpperCase()}</div>
                  <div className="p-4 flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('factuurnummer')}:</span>
                    <span
                      className="font-bold text-blue-700 text-lg cursor-pointer hover:text-blue-500 hover:underline"
                      onClick={() => setShowFactuurPdf(detailAanvraag.FactuurNr)}
                      title="Klik om factuur te bekijken"
                    >
                      {detailAanvraag.FactuurNr}
                    </span>
                  </div>
                </div>
              )}

              {/* Opmerkingen */}
              {detailAanvraag.Opmerkingen && (
                <div className="border-2 border-blue-900 overflow-hidden">
                  <div className="bg-blue-900 text-white px-4 py-2 font-bold text-sm">{t('opmerkingen').toUpperCase()}</div>
                  <div className="p-4 text-sm text-gray-700">{detailAanvraag.Opmerkingen}</div>
                </div>
              )}

              {/* Datums overzicht */}
              <div className="border-2 border-blue-900 overflow-hidden">
                <div className="bg-blue-900 text-white px-4 py-2 font-bold text-sm">{t('overzicht').toUpperCase()}</div>
                <div className="p-4 space-y-2 text-sm">
                  <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">{t('aanvraagdatum')}:</span><span className="font-medium">{formatDate(detailAanvraag.Datum)}</span></div>
                  <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">{t('status')}:</span><span className="font-bold text-blue-900">{detailAanvraag.KlantStatus || detailAanvraag.Status || '-'}</span></div>
                  {detailAanvraag.KlantBevestigdOp && <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">{t('status_akkoord_gegeven')}:</span><span className="font-medium text-green-700">{formatDate(detailAanvraag.KlantBevestigdOp)}</span></div>}
                  {detailAanvraag.Referentie1 && <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">Referentie 1:</span><span className="font-medium">{detailAanvraag.Referentie1}</span></div>}
                  {detailAanvraag.Referentie2 && <div className="flex justify-between"><span className="text-gray-500">Referentie 2:</span><span className="font-medium">{detailAanvraag.Referentie2}</span></div>}
                </div>
              </div>
              {/* Annuleringsreden */}
              {detailAanvraag.AfschrijfReden && (
                <div className="border-2 border-red-600 overflow-hidden">
                  <div className="bg-red-600 text-white px-4 py-2 font-bold text-sm">{t('status_geannuleerd').toUpperCase()}</div>
                  <div className="p-4">
                    <div className="flex justify-between mb-1"><span className="text-gray-500 text-sm">{t('reden')}:</span><span className="font-semibold text-red-700">{detailAanvraag.AfschrijfReden}</span></div>
                    {detailAanvraag.AfschrijfDatum && <div className="flex justify-between"><span className="text-gray-500 text-sm">{t('datum')}:</span><span className="font-medium">{detailAanvraag.AfschrijfDatum}</span></div>}
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-gray-100 p-4 border-t-2 border-blue-900 flex justify-end">
              <button onClick={() => setDetailAanvraag(null)} className="px-8 py-2.5 bg-blue-900 text-white font-semibold hover:bg-blue-800 transition-colors">{t('sluiten')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Factuur PDF popup */}
      {showFactuurPdf && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10001 }} onClick={() => setShowFactuurPdf('')}>
          <div className="bg-white shadow-2xl w-full max-w-4xl border-2 border-blue-900" style={{ height: '85vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="bg-blue-900 text-white p-4 flex justify-between items-center">
              <h3 className="font-bold">Factuur {showFactuurPdf}</h3>
              <button onClick={() => setShowFactuurPdf('')} className="w-8 h-8 flex items-center justify-center hover:bg-blue-800 text-xl">&times;</button>
            </div>
            <iframe
              src={'/api/customer/factuur-pdf/' + showFactuurPdf}
              className="w-full border-0"
              style={{ height: 'calc(85vh - 56px)' }}
              title="Factuur"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAanvragenModal;
